import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { useVariableSources } from '@/components/admin/experiences/experience-variable-scope';
import { ExperiencePreviewButton } from '@/components/admin/experiences/preview/preview-button';
import { ExperienceThemeBar } from '@/components/admin/experiences/theme/experience-theme-bar';
import { SegmentedControl } from '@/components/admin/experiences/theme/segmented-control';
import { ExperienceRail } from '@/components/admin/experiences/timeline/experience-rail';
import { ExperienceScene } from '@/components/admin/experiences/timeline/experience-scene';
import { ExperienceProtocolPicker } from '@/components/admin/experiences/timeline/protocol-picker';
import type { ReaderFormat } from '@/components/reader';
import { useAdminSession } from '@/lib/admin-session';
import { fetchProtocols } from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import {
  addFeedbackPage,
  feedbackPageRefusal,
  insertPageAt,
  insertedPageIndex,
  removePage,
  selectedPage,
  withPageProtocol,
  withTheme,
} from '@/lib/experience-draft';
import { duplicatePage, pageDuplicateRefusal } from '@/lib/experience-duplicate';
import { previewVariableNames } from '@/lib/variable-pill';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

/**
 * L'onglet Timeline : la barre « Visuel global », la scène et le rail (refonte
 * modulaire §9.1, §9.4, §9.5 ; maquette `Timeline.dc.html` ; issue #57).
 *
 * Three surfaces and almost no logic of its own. Every page edit it performs is
 * a pure function of `experience-draft.ts` - the page editor of #58 performs
 * several of the same ones from another screen - and everything the bar says is
 * a pure function of `experience-theme.ts`, which the save box of §9.1 reads
 * too.
 *
 * ## What travels in the URL, and what does not
 *
 * The SELECTED PAGE does (§9.1, `?page=`), and it is the editor above that
 * writes it: this tab asks by callback, exactly as a refused line of the save
 * box does, so one place in the screen knows the route. The FORMAT does not -
 * « le format Téléphone | Écran est un état local de l'onglet » - because it
 * says how somebody is LOOKING at the experience and not what the experience
 * is; the arbitrage `protocol-timeline.tsx` makes for its zoom.
 *
 * ## `['protocols', email]`, the key the editor already writes
 *
 * Deliberately the same: same fetcher, same schema, same shape, so the protocol
 * page's card and the save box's production check read ONE list. The trap of
 * issue #16 is two readers agreeing on a key and disagreeing on what is under
 * it, which is the opposite of this.
 *
 * Nothing is claimed before that list has landed: a protocol page whose
 * reference resolved to nothing would otherwise read « Protocole introuvable »
 * for the first frame of every opening, on a perfectly healthy protocol.
 */

export interface ExperienceTimelineTabProps {
  readonly draft: ExperienceDraft;
  /** A version opened by « Voir », or a retired experience: nothing is typable. */
  readonly readOnly: boolean;
  /** The entity's id, or null before the first save - see `ExperienceScene`. */
  readonly experienceId: string | null;
  /** The page `?page=` names, or null - `selectedPage` resolves an unknown one. */
  readonly selectedPageId: string | null;
  readonly onDraft: (next: ExperienceDraft) => void;
  /** Asks the editor to write `?page=`; the tab never touches the route (§9.1). */
  readonly onSelectPage: (pageId: string) => void;
}

export function ExperienceTimelineTab({
  draft,
  readOnly,
  experienceId,
  selectedPageId,
  onDraft,
  onSelectPage,
}: ExperienceTimelineTabProps) {
  const { email } = useAdminSession();
  const [format, setFormat] = useState<ReaderFormat>('screen');
  /** Which protocol page the selector is choosing for, or null when it is closed. */
  const [picking, setPicking] = useState<string | null>(null);

  const protocols = useQuery({
    queryKey: ['protocols', email],
    queryFn: fetchProtocols,
    retry: false,
  });

  const selected = selectedPage(draft, selectedPageId);
  const library = protocols.data?.protocols ?? [];

  /*
    Ce que la scène NOMME (§8.6, D5) : le lecteur y peint chaque jeton en
    pastille au nom courant, jamais une valeur - une valeur inventée ferait
    croire à une donnée. Les noms des objectifs viennent du protocole que la
    portée de l'écran a lu ; hors de toute portée, ils sont « inconnus ».
  */
  const sources = useVariableSources(draft);
  const variableNames = useMemo(() => previewVariableNames(sources), [sources]);

  /** A « + » on an edge: insert at the gap, then PUT THE NEW PAGE AT THE CENTRE. */
  const insert = (index: number): void => {
    const next = insertPageAt(draft, index);
    if (next === draft) return;
    onDraft(next);
    /*
      The page somebody just created is the page they want to look at, and the
      scene shows exactly one. The id is read out of the NEW draft rather than
      minted here, so `insertPageAt` stays the only place a page is born.
    */
    const born = next.pages[insertedPageIndex(draft, index)];
    if (born !== undefined) onSelectPage(born.id);
  };

  /**
   * « Ajouter une page » at the end of the line: the page lands BEFORE the
   * feedback page when there is one (`insertPageAt` clamps it, §7.1) and is the
   * one the scene then shows.
   */
  const append = (): void => insert(draft.pages.length);

  /**
   * « Page de retour » of the menu of the end (§7.1): the feedback page of the
   * template, last, and selected. A refused gesture (the ceiling) answers the
   * draft unchanged and navigates nowhere, like the two above.
   */
  const appendFeedback = (): void => {
    const next = addFeedbackPage(draft);
    if (next === draft) return;
    onDraft(next);
    const born = next.pages[next.pages.length - 1];
    if (born !== undefined) onSelectPage(born.id);
  };

  /** The menu of the end exists while there is no feedback page to add before (§7.1). */
  const hasFeedback = draft.pages.some((one) => one.kind === 'feedback');

  /**
   * « Dupliquer » : la copie se pose juste après sa source, PUIS la scène la
   * montre (§4.2).
   *
   * La sélection suit la copie et pas la source, comme après une insertion :
   * « la copie est sélectionnée après le geste ». L'identifiant est lu dans le
   * résultat plutôt que frappé ici, pour que `duplicatePage` reste le seul
   * endroit où une copie naît - la règle que `insert` tient déjà pour une page
   * neuve. Un geste refusé (le plafond, la page protocole) rend le brouillon
   * inchangé et ne navigue nulle part.
   */
  const duplicate = (pageId: string): void => {
    const next = duplicatePage(draft, pageId);
    if (next.pageId === null) return;
    onDraft(next.draft);
    onSelectPage(next.pageId);
  };

  /**
   * Removes a page and moves the selection WITH it.
   *
   * `?page=` would otherwise name a page that no longer exists. Nothing breaks
   * - `selectedPage` degrades an unknown id onto the first page - but the scene
   * would jump to the beginning of the line from wherever somebody was. The
   * previous page is where the eye already is.
   */
  const remove = (pageId: string): void => {
    const next = removePage(draft, pageId);
    if (next === draft) return;
    const at = draft.pages.findIndex((one) => one.id === pageId);
    const fallback = next.pages[Math.max(at - 1, 0)];
    onDraft(next);
    if (fallback !== undefined) onSelectPage(fallback.id);
  };

  return (
    <section data-testid={EXPERIENCE_IDS.timeline} className="space-y-4">
      <ExperienceThemeBar
        level="global"
        value={draft.theme}
        readOnly={readOnly}
        onChange={(theme) => onDraft(withTheme(draft, theme))}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[95ch] text-gray-600 text-xs leading-4">
          La page au centre est rendue comme dans le lecteur et défile si elle est longue ; la
          précédente et la suivante sont entières, en petit ; le rail du bas dit où l’on est et mène
          à n’importe quelle page.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl<ReaderFormat>
            label="Format de l’aperçu"
            testId={EXPERIENCE_IDS.format}
            options={[
              { value: 'phone', label: 'Téléphone' },
              { value: 'screen', label: 'Écran' },
            ]}
            value={format}
            onChange={setFormat}
          />
          {/* La porte de l'aperçu plein écran (#141) : elle ouvre sur la page
              que le rail a sélectionnée, et tout ce qu'elle a besoin de tenir
              vit chez elle. */}
          <ExperiencePreviewButton
            draft={draft}
            pageId={selected?.id ?? null}
            format={format}
            variableNames={variableNames}
          />
        </div>
      </div>

      {selected === null ? (
        <p className="text-gray-700 text-sm">Cette expérience ne porte aucune page.</p>
      ) : (
        <>
          <ExperienceScene
            draft={draft}
            selected={selected}
            format={format}
            readOnly={readOnly}
            experienceId={experienceId}
            protocols={library}
            protocolsRead={protocols.data !== undefined}
            onSelectPage={onSelectPage}
            onInsert={insert}
            onDelete={remove}
            onDuplicate={duplicate}
            duplicateRefusal={pageDuplicateRefusal(draft)}
            onReplaceProtocol={() => setPicking(selected.id)}
            variableNames={variableNames}
            onAddFeedbackPage={hasFeedback ? undefined : appendFeedback}
            feedbackRefusal={feedbackPageRefusal(draft)}
          />

          <ExperienceRail
            pages={draft.pages}
            selectedPageId={selected.id}
            readOnly={readOnly}
            onSelectPage={onSelectPage}
            onAddPage={append}
            onAddFeedbackPage={hasFeedback ? undefined : appendFeedback}
            feedbackRefusal={feedbackPageRefusal(draft)}
          />
        </>
      )}

      {picking === null ? null : (
        <ExperienceProtocolPicker
          protocols={library}
          loaded={protocols.data !== undefined}
          chosenId={draft.pages.find((one) => one.id === picking)?.protocol?.id ?? null}
          onChoose={(protocolId) => {
            // A new choice is `latest`: épingler est l'exception (V16).
            onDraft(withPageProtocol(draft, picking, { id: protocolId, pin: 'latest' }));
            setPicking(null);
          }}
          onClear={() => {
            onDraft(withPageProtocol(draft, picking, null));
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}

      <p className="max-w-[95ch] text-gray-600 text-sm leading-5">
        Une expérience est une ligne : pas de branchement, c’est une variable qui porte un choix
        pour les pages suivantes. La page protocole est obligatoire et unique ; la page de retour,
        facultative et unique, vient toujours en dernier.
      </p>
    </section>
  );
}
